import { useQueryClient } from "@tanstack/react-query";
import React from "react";
import { useOrgConfigKey } from "src/generated/hooks/org-config/queries";
import * as OrgConfig from "src/generated/hooks/org-config/requests";
import { isNonEmptyString } from "src/services/predicates";
import * as RD from "src/types/remoteData";
import { DeepReadonly } from "src/types/utils";
import { useOrganizationPath } from "./useOrganizationPath";
import { useRemoteDataRestQuery } from "./useRemoteDataQuery";

// With defaultValue → data is always DATA
export function useOrgConfig<
  DATA extends OrgConfig.OrgConfig,
  T extends OrgConfig.OrgConfigType = OrgConfig.OrgConfigType
>(
  type: T,
  defaultValue: DeepReadonly<DATA>
): RD.RemoteData<Error, DeepReadonly<DATA>>;

// Without defaultValue → data may be undefined
export function useOrgConfig<
  DATA extends OrgConfig.OrgConfig,
  T extends OrgConfig.OrgConfigType = OrgConfig.OrgConfigType
>(
  type: T,
  defaultValue?: DeepReadonly<DATA>
): RD.RemoteData<Error, DeepReadonly<DATA> | null>;

// Implementation
export function useOrgConfig<DATA extends OrgConfig.OrgConfig>(
  type: OrgConfig.OrgConfigType,
  defaultValue?: DeepReadonly<DATA>
):
  | RD.RemoteData<Error, DeepReadonly<DATA>>
  | RD.RemoteData<Error, DeepReadonly<DATA> | null> {
  const org = useOrganizationPath();

  const remoteConfig = useRemoteDataRestQuery({
    enabled: isNonEmptyString(org),
    // Org config is effectively static for the lifetime of a session, and these
    // queries back providers that gate the whole app, so a background refetch is
    // all cost and no benefit.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    queryKey: [useOrgConfigKey, type, org],
    queryFn: async () => {
      const resp = await OrgConfig.orgConfig({
        path: {
          organizationPath: org ?? "",
          configType: type,
        },
      });
      return resp.data ?? defaultValue ?? null;
    },
  });

  return remoteConfig as
    | RD.RemoteData<Error, DeepReadonly<DATA>>
    | RD.RemoteData<Error, DeepReadonly<DATA> | null>;
}

// Provides helpers for invalidating cached org config with better type support for `OrgConfig.OrgConfigType`
export function useInvalidateOrgConfig() {
  const queryClient = useQueryClient();
  const org = useOrganizationPath();

  const invalidateOrgConfig = React.useCallback(
    async (configType: OrgConfig.OrgConfigType) => {
      await queryClient.invalidateQueries({
        queryKey: [useOrgConfigKey, configType, org],
      });
    },
    [queryClient, org]
  );

  // Schedules an org config invalidation to run after a delay.
  // Useful since CDN take time to propagate before the updated config is
  // available, so we wait before refetching.
  const scheduleOrgConfigInvalidation = React.useCallback(
    async (
      configType: OrgConfig.OrgConfigType,
      { delayInMs }: { delayInMs: number }
    ) => {
      setTimeout(async () => {
        await invalidateOrgConfig(configType);
      }, delayInMs);
    },
    [invalidateOrgConfig]
  );
  return {
    invalidateOrgConfig,
    scheduleOrgConfigInvalidation,
  };
}
